import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Search, Filter, Sparkles, RefreshCw, Zap, Bot, Puzzle, FolderPlus, Folder, ChevronDown, Pencil, Trash2, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAuth } from '@/contexts/SharedAuthContext';
import { canViewSkill, getUserTeam } from '@/lib/skills-permissions';
import { SkillAgentCard } from '@/components/skills/SkillAgentCard';
import { DropSkillZone } from '@/components/skills/DropSkillZone';
import { SkillsAnalyticsTab } from '@/components/skills/SkillsAnalyticsTab';
import { NewFolderModal } from '@/components/skills/NewFolderModal';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { SkillTeam } from '../../../shared/types';

const ALL_TEAMS: SkillTeam[] = [
  'Business BR', 'Business SPLA', 'Business',
  'Compensations and Experience', 'Creative Studio', 'Data', 'Development',
  'Enterprise', 'Finance', 'Growth Partners', 'Growth', 'IT', 'Marketing',
  'New Solutions', 'Operations', 'Owned Media Lab', 'Paid Media Lab', 'People', 'Performance',
  'Retail Media', 'Sales', 'Sharing', 'Strategy & Ops', 'Strategy & Planning',
  'Startup', 'Talent Acquisition', 'Tech & Product', 'Tech', 'US', 'We Care',
];

export function SkillsAgentsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();

  const [activeTab, setActiveTab] = useState('guide');
  const [searchQuery, setSearchQuery] = useState('');
  const [teamFilter, setTeamFilter] = useState<string>('all');
  const [showFilters, setShowFilters] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [collapsedFolders, setCollapsedFolders] = useState<Set<string> | 'all'>('all');
  const [renamingFolderId, setRenamingFolderId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingFolderId, setDeletingFolderId] = useState<string | null>(null);
  const [dragOverFolderId, setDragOverFolderId] = useState<string | null>(null);
  const [newSubfolderParent, setNewSubfolderParent] = useState<{ id: string; name: string } | null>(null);

  const toggleFolder = (id: string) =>
    setCollapsedFolders((prev) => {
      const next = prev === 'all' ? new Set(folders.map((f) => f.id)) : new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const { data: skills = [], isLoading } = trpc.skills.getAll.useQuery();
  const { data: folders = [] } = trpc.skills.getFolders.useQuery();
  const utils = trpc.useUtils();

  const syncMutation = trpc.skills.syncFromRepo.useMutation({
    onSuccess: (result) => {
      if (result.errors.length > 0) {
        toast.warning(`Sincronizado: ${result.upserted} skills. Errores: ${result.errors.join(' | ')}`);
      } else {
        toast.success(`Sincronizado: ${result.upserted} skills importadas`);
      }
      utils.skills.getAll.invalidate();
    },
    onError: (err) => toast.error(`Error al sincronizar: ${err.message}`),
  });

  const deleteFolderMutation = trpc.skills.deleteFolder.useMutation({
    onSuccess: () => { utils.skills.getFolders.invalidate(); toast.success(t('skills.page.carpeta_eliminada')); setDeletingFolderId(null); },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const renameFolderMutation = trpc.skills.renameFolder.useMutation({
    onSuccess: () => { utils.skills.getFolders.invalidate(); setRenamingFolderId(null); },
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const addToFolderDragMutation = trpc.skills.addSkillToFolder.useMutation({
    onSuccess: () => utils.skills.getFolders.invalidate(),
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const removeFromFolderMutation = trpc.skills.removeSkillFromFolder.useMutation({
    onSuccess: () => utils.skills.getFolders.invalidate(),
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const moveFolderMutation = trpc.skills.moveFolder.useMutation({
    onSuccess: () => utils.skills.getFolders.invalidate(),
    onError: (err) => toast.error(`Error: ${err.message}`),
  });

  const handleDrop = (e: React.DragEvent, targetFolderId: string | null) => {
    e.preventDefault();
    setDragOverFolderId(null);

    const dragType = e.dataTransfer.getData('dragType');

    if (dragType === 'folder') {
      const folderId = e.dataTransfer.getData('folderId');
      if (!folderId || folderId === targetFolderId) return;
      // Only allow dropping INTO a root folder (not into itself or into a subfolder)
      const targetIsChild = !!folders.find((f) => f.id === targetFolderId)?.parentFolderId;
      if (targetIsChild) return;
      moveFolderMutation.mutate({ folderId, parentFolderId: targetFolderId });
      return;
    }

    const skillId = e.dataTransfer.getData('skillId');
    const fromFolderId = e.dataTransfer.getData('fromFolderId') || null;
    if (!skillId) return;
    if (targetFolderId === (fromFolderId || null)) return;
    if (fromFolderId) removeFromFolderMutation.mutate({ folderId: fromFolderId, skillId });
    if (targetFolderId) addToFolderDragMutation.mutate({ folderId: targetFolderId, skillId });
  };

  const startRename = (e: React.MouseEvent, folder: typeof folders[0]) => {
    e.stopPropagation();
    setRenamingFolderId(folder.id);
    setRenameValue(folder.name);
    setDeletingFolderId(null);
  };

  const confirmRename = (folderId: string) => {
    const trimmed = renameValue.trim();
    if (trimmed) renameFolderMutation.mutate({ folderId, newName: trimmed });
    else setRenamingFolderId(null);
  };

  const currentUser = user
    ? { email: user.email, isAdmin: (user.roles ?? []).includes('admin'), roles: user.roles ?? (user.role ? [user.role] : []) }
    : null;

  const isAdminOrGrowth = (user?.roles ?? []).includes('admin') || (user?.roles ?? [user?.role]).includes('growth');
  const isTechOrAdmin = (user?.roles ?? []).includes('admin') || (user?.roles ?? []).includes('tech');

  const userTeam = getUserTeam(currentUser);

  const visibleSkills = useMemo(
    () => skills.filter((s) => canViewSkill(currentUser, s)),
    [skills, currentUser]
  );

  const filteredSkills = useMemo(() => {
    let result = visibleSkills;

    if (activeTab === 'official') {
      result = result.filter((s) => s.source === 'official');
    } else if (activeTab === 'community') {
      result = result.filter((s) => s.source === 'community');
    } else if (activeTab === 'favorites') {
      result = result.filter((s) => s.isFavorite);
    } else if (activeTab === 'by_team') {
      const team = teamFilter !== 'all' ? teamFilter : userTeam;
      if (team) result = result.filter((s) => s.team === team);
    }

    if (teamFilter !== 'all' && activeTab !== 'by_team') {
      result = result.filter((s) => s.team === teamFilter);
    }

    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.description.toLowerCase().includes(q) ||
          s.tags.some((tag) => tag.toLowerCase().includes(q)) ||
          s.author.toLowerCase().includes(q)
      );
    }

    return result;
  }, [visibleSkills, activeTab, searchQuery, teamFilter, userTeam]);

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Sparkles className="h-7 w-7 text-primary" />
            {t('skills.page_title')}
          </h2>
          <p className="text-muted-foreground mt-1">
            {t('skills.page_subtitle')}
          </p>
        </div>
        <div className="flex gap-2">
          {isAdminOrGrowth && (
            <Button
              variant="outline"
              onClick={() => syncMutation.mutate({ repoOwner: 'winclap', repoName: 'winclap-skills' })}
              disabled={syncMutation.isPending}
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${syncMutation.isPending ? 'animate-spin' : ''}`} />
              {t('skills.page.sincronizar_repo')}
            </Button>
          )}
          <Button variant="outline" onClick={() => setShowNewFolder(true)}>
            <FolderPlus className="h-4 w-4 mr-2" />
            {t('skills.folders.new_btn')}
          </Button>
          <Button onClick={() => navigate('/skills-agentes/new')}>
            <Plus className="h-4 w-4 mr-2" />
            {t('skills.new_skill_btn')}
          </Button>
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="guide">{t('skills.tabs.guide')}</TabsTrigger>
            <TabsTrigger value="all">{t('skills.tabs.all')}</TabsTrigger>
            <TabsTrigger value="official">{t('skills.tabs.official')}</TabsTrigger>
            <TabsTrigger value="community">{t('skills.tabs.community')}</TabsTrigger>
            <TabsTrigger value="by_team">{t('skills.tabs.by_team')}</TabsTrigger>
            <TabsTrigger value="favorites">{t('skills.tabs.favorites')}</TabsTrigger>
            {isTechOrAdmin && (
              <TabsTrigger value="analytics">{t('skills.analytics.tab')}</TabsTrigger>
            )}
          </TabsList>

          {/* Search + filters */}
          <div className="flex gap-2 flex-1 sm:max-w-md">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('skills.search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant={showFilters ? 'default' : 'outline'}
              size="icon"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Filter panel */}
        {showFilters && (
          <div className="mt-3 flex items-center gap-3 rounded-lg bg-muted/40 p-3">
            <span className="text-sm font-medium text-muted-foreground">Equipo:</span>
            <Select value={teamFilter} onValueChange={setTeamFilter}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder={t('skills.page.todos_los_equipos')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('skills.all_teams')}</SelectItem>
                {ALL_TEAMS.map((team) => (
                  <SelectItem key={team} value={team}>{team}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Guide tab */}
        <TabsContent value="guide" className="mt-4">
          <div className="space-y-5 max-w-2xl">

            {/* Step 1 */}
            <div className="rounded-2xl border bg-linear-to-br from-violet-50 to-blue-50 dark:from-violet-950/40 dark:to-blue-950/40 p-5 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-violet-500 text-white text-xs font-bold">1</div>
                <div>
                  <p className="font-semibold">{t('skills.guide.step1_title')}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {t('skills.guide.step1_desc')}
                  </p>
                </div>
              </div>
              <pre className="text-xs bg-background/80 rounded-xl p-3 border whitespace-pre-wrap font-mono leading-relaxed">
                {t('skills.guide.step1_prompt')}
              </pre>
              <Button
                variant="outline"
                size="sm"
                className="bg-background/80 hover:bg-background w-full"
                onClick={async () => {
                  await navigator.clipboard.writeText(t('skills.guide.step1_prompt'));
                  toast.success(t('skills.guide.copy_prompt_toast'));
                }}
              >
                {t('skills.guide.copy_prompt_btn')}
              </Button>
            </div>

            {/* Step 2 */}
            <div className="rounded-2xl border bg-muted/30 p-5 space-y-3">
              <div className="flex items-start gap-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-foreground text-background text-xs font-bold">2</div>
                <div>
                  <p className="font-semibold">{t('skills.guide.step2_title')}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">
                    {t('skills.guide.step2_desc')}
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate('/skills-agentes/new')} className="w-full">
                <Plus className="h-4 w-4 mr-2" />
                {t('skills.form.upload_btn')}
              </Button>
            </div>

            {/* Info cards */}
            <div className="space-y-2 pt-1">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">{t('skills.guide.what_is')}</p>
              {[
                {
                  icon: <Zap className="h-4 w-4 text-yellow-500" />,
                  title: t('skills.card.type_skill'),
                  desc: t('skills.guide.skill_desc'),
                  examples: t('skills.guide.skill_examples'),
                },
                {
                  icon: <Bot className="h-4 w-4 text-blue-500" />,
                  title: t('skills.card.type_agent'),
                  desc: t('skills.guide.agent_desc'),
                  examples: t('skills.guide.agent_examples'),
                },
                {
                  icon: <Puzzle className="h-4 w-4 text-violet-500" />,
                  title: t('skills.card.type_plugin'),
                  desc: t('skills.guide.plugin_desc'),
                  examples: t('skills.guide.plugin_examples'),
                },
              ].map((item) => (
                <div key={item.title} className="flex items-start gap-3 rounded-xl border bg-card p-3.5">
                  <div className="mt-0.5 shrink-0">{item.icon}</div>
                  <div>
                    <p className="text-sm font-semibold">{item.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">{item.desc}</p>
                    <p className="text-xs text-muted-foreground/70 mt-0.5 italic">{item.examples}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* Analytics tab — tech/admin only */}
        {isTechOrAdmin && (
          <TabsContent value="analytics">
            <SkillsAnalyticsTab />
          </TabsContent>
        )}

        {/* Tab content — all non-guide/analytics tabs share this renderer */}
        {activeTab !== 'guide' && activeTab !== 'analytics' && (
        <TabsContent value={activeTab} className="mt-4">
          {isLoading ? (
            <div className="flex justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : filteredSkills.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center space-y-2">
              <Sparkles className="h-12 w-12 text-muted-foreground/40" />
              <p className="font-semibold text-muted-foreground">
                {t('skills.empty.title')}
              </p>
              <p className="text-sm text-muted-foreground">
                {activeTab === 'favorites'
                  ? t('skills.empty.favorites')
                  : t('skills.empty.subtitle')}
              </p>
              {activeTab !== 'favorites' && (
                <Button
                  variant="outline"
                  className="mt-2"
                  onClick={() => navigate('/skills-agentes/new')}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  {t('skills.empty.create_btn')}
                </Button>
              )}
            </div>
          ) : (() => {
            if (folders.length === 0) {
              return (
                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {filteredSkills.map((skill) => (
                    <SkillAgentCard key={skill.id} skill={skill} currentUser={currentUser} folders={folders} />
                  ))}
                  <DropSkillZone />
                </div>
              );
            }

            const rootFolders = folders.filter((f) => !f.parentFolderId);
            const childFolders = folders.filter((f) => !!f.parentFolderId);
            const childrenByParent = childFolders.reduce<Record<string, typeof folders>>((acc, f) => {
              const pid = f.parentFolderId!;
              acc[pid] = acc[pid] ? [...acc[pid], f] : [f];
              return acc;
            }, {});

            const usedIds = new Set<string>();
            folders.forEach((f) => f.skillIds.forEach((id) => usedIds.add(id)));
            const ungrouped = filteredSkills.filter((s) => !usedIds.has(s.id));

            const renderFolderHeader = (folder: typeof folders[0], isChild = false) => {
              const isRenaming = renamingFolderId === folder.id;
              const isConfirmingDelete = deletingFolderId === folder.id;
              if (isRenaming) return (
                <div className="flex items-center gap-1.5 flex-1" onClick={(e) => e.stopPropagation()}>
                  <Folder className={cn('shrink-0', isChild ? 'h-3.5 w-3.5 text-muted-foreground' : 'h-4 w-4 text-primary')} />
                  <input
                    autoFocus
                    className="text-sm font-semibold border border-input rounded px-2 py-0.5 flex-1 bg-background focus:outline-hidden focus:ring-1 focus:ring-primary"
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') confirmRename(folder.id); if (e.key === 'Escape') setRenamingFolderId(null); }}
                    maxLength={50}
                  />
                  <button onClick={() => confirmRename(folder.id)} disabled={renameFolderMutation.isPending} className="text-primary hover:text-primary/80 p-0.5"><Check className="h-3.5 w-3.5" /></button>
                  <button onClick={() => setRenamingFolderId(null)} className="text-muted-foreground hover:text-foreground p-0.5"><X className="h-3.5 w-3.5" /></button>
                </div>
              );
              if (isConfirmingDelete) return (
                <div className="flex items-center gap-2 flex-1 text-sm" onClick={(e) => e.stopPropagation()}>
                  <Folder className="h-4 w-4 text-destructive shrink-0" />
                  <span className="text-destructive font-medium">{t('skills.page.eliminar_name', { name: folder.name })}</span>
                  <button onClick={() => deleteFolderMutation.mutate({ folderId: folder.id })} disabled={deleteFolderMutation.isPending} className="ml-1 text-xs font-semibold text-destructive border border-destructive/50 rounded px-2 py-0.5 hover:bg-destructive/10">Eliminar</button>
                  <button onClick={() => setDeletingFolderId(null)} className="text-xs text-muted-foreground hover:text-foreground">Cancelar</button>
                </div>
              );
              const skillCount = filteredSkills.filter((s) => folder.skillIds.includes(s.id)).length;
              return (
                <>
                  <button
                    onClick={() => toggleFolder(folder.id)}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData('dragType', 'folder');
                      e.dataTransfer.setData('folderId', folder.id);
                      e.dataTransfer.effectAllowed = 'move';
                      e.stopPropagation();
                    }}
                    className="flex items-center gap-2 group hover:text-primary transition-colors text-left flex-1 min-w-0 cursor-grab active:cursor-grabbing"
                  >
                    <Folder className={cn('shrink-0', isChild ? 'h-3.5 w-3.5 text-muted-foreground' : 'h-4 w-4 text-primary')} />
                    <span className={cn('font-semibold truncate', isChild ? 'text-xs' : 'text-sm')}>{folder.name}</span>
                    <span className="text-xs text-muted-foreground shrink-0">{skillCount}</span>
                    <ChevronDown className={cn('h-3.5 w-3.5 text-muted-foreground ml-1 transition-transform shrink-0', (collapsedFolders === 'all' || (collapsedFolders instanceof Set && collapsedFolders.has(folder.id))) && '-rotate-90')} />
                  </button>
                  {!isChild && (
                    <button onClick={(e) => { e.stopPropagation(); setNewSubfolderParent({ id: folder.id, name: folder.name }); }} className="p-1 text-muted-foreground hover:text-primary rounded transition-colors shrink-0" title={t('skills.page.nueva_subcarpeta')}>
                      <FolderPlus className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button onClick={(e) => startRename(e, folder)} className="p-1 text-muted-foreground hover:text-foreground rounded transition-colors shrink-0" title="Renombrar"><Pencil className="h-3.5 w-3.5" /></button>
                  <button onClick={(e) => { e.stopPropagation(); setDeletingFolderId(folder.id); setRenamingFolderId(null); }} className="p-1 text-muted-foreground hover:text-destructive rounded transition-colors shrink-0" title="Eliminar"><Trash2 className="h-3.5 w-3.5" /></button>
                </>
              );
            };

            const renderSkillsGrid = (folder: typeof folders[0] | null, skillsList: typeof filteredSkills) => {
              const fid = folder?.id ?? '__ungrouped__';
              const isDragOver = dragOverFolderId === fid;
              return (
                <div
                  onDragOver={(e) => { e.preventDefault(); setDragOverFolderId(fid); }}
                  onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverFolderId(null); }}
                  onDrop={(e) => handleDrop(e, folder?.id ?? null)}
                  className={cn('rounded-lg transition-colors', isDragOver && 'ring-2 ring-primary/40 bg-primary/5')}
                >
                  <div className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-3', isDragOver && 'pt-1')}>
                    {skillsList.map((skill) => (
                      <SkillAgentCard key={skill.id} skill={skill} currentUser={currentUser} folders={folders}
                        activeFolderId={folder?.id}
                        onRemoveFromFolder={folder ? () => removeFromFolderMutation.mutate({ folderId: folder.id, skillId: skill.id }) : undefined}
                      />
                    ))}
                    {folder && skillsList.length === 0 && (
                      <div className={cn('col-span-full flex items-center justify-center rounded-lg border-2 border-dashed h-16 text-sm text-muted-foreground transition-colors', isDragOver ? 'border-primary/60 text-primary bg-primary/5' : 'border-muted-foreground/20')}>
                        {isDragOver ? t('skills.page.soltar_aqui') : t('skills.page.vacia_arrastra_skills_aqui')}
                      </div>
                    )}
                    {!folder && <DropSkillZone />}
                  </div>
                </div>
              );
            };

            return (
              <div className="space-y-6">
                {rootFolders.map((folder) => {
                  const collapsed = collapsedFolders === 'all' || (collapsedFolders instanceof Set && collapsedFolders.has(folder.id));
                  const children = childrenByParent[folder.id] ?? [];
                  const directSkills = filteredSkills.filter((s) => folder.skillIds.includes(s.id));
                  const isFolderDragOver = dragOverFolderId === `folder-header-${folder.id}`;
                  return (
                    <div
                      key={folder.id}
                      className="space-y-1"
                      onDragOver={(e) => {
                        // Accept folder drops on the header area
                        e.preventDefault();
                        setDragOverFolderId(`folder-header-${folder.id}`);
                      }}
                      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverFolderId(null); }}
                      onDrop={(e) => {
                        const dt = e.dataTransfer.getData('dragType');
                        if (dt === 'folder') { handleDrop(e, folder.id); setDragOverFolderId(null); }
                      }}
                    >
                      <div className={cn('flex items-center gap-2 mb-2 rounded-md px-1 -mx-1 transition-colors', isFolderDragOver && 'bg-primary/10 ring-1 ring-primary/30')}>
                        {renderFolderHeader(folder, false)}
                      </div>
                      {!collapsed && (
                        <div className="space-y-4">
                          {directSkills.length > 0 && renderSkillsGrid(folder, directSkills)}
                          {children.map((child) => {
                            // Acá el padre ya no está colapsado, así que collapsedFolders no puede ser 'all'.
                            const childCollapsed = collapsedFolders instanceof Set && collapsedFolders.has(child.id);
                            const childSkills = filteredSkills.filter((s) => child.skillIds.includes(s.id));
                            return (
                              <div key={child.id} className="ml-5 border-l border-border pl-4 space-y-2">
                                <div className="flex items-center gap-2">
                                  {renderFolderHeader(child, true)}
                                </div>
                                {!childCollapsed && renderSkillsGrid(child, childSkills)}
                              </div>
                            );
                          })}
                          {directSkills.length === 0 && children.length === 0 && renderSkillsGrid(folder, [])}
                        </div>
                      )}
                    </div>
                  );
                })}
                {ungrouped.length > 0 && (
                  <div
                    onDragOver={(e) => { e.preventDefault(); setDragOverFolderId('__ungrouped__'); }}
                    onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverFolderId(null); }}
                    onDrop={(e) => handleDrop(e, null)}
                  >
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {ungrouped.map((skill) => (
                        <SkillAgentCard key={skill.id} skill={skill} currentUser={currentUser} folders={folders} />
                      ))}
                      <DropSkillZone />
                    </div>
                  </div>
                )}
                {ungrouped.length === 0 && rootFolders.length > 0 && (
                  <div onDragOver={(e) => { e.preventDefault(); setDragOverFolderId('__ungrouped__'); }} onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setDragOverFolderId(null); }} onDrop={(e) => handleDrop(e, null)}>
                    <DropSkillZone />
                  </div>
                )}
              </div>
            );
          })()}
        </TabsContent>
        )}
      </Tabs>

      <NewFolderModal open={showNewFolder} onClose={() => setShowNewFolder(false)} />
      <NewFolderModal
        open={!!newSubfolderParent}
        onClose={() => setNewSubfolderParent(null)}
        parentFolderId={newSubfolderParent?.id}
        parentFolderName={newSubfolderParent?.name}
      />
    </div>
  );
}
